'use client';

import { ArrowLeft, ArrowRight } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import type { MarketingShowcaseMediaAsset } from './content';

export function LandingShowcaseCarousel({
  assets,
  alt,
  fallbackTitle,
  fallbackDescription,
  previousLabel,
  nextLabel,
}: {
  assets: MarketingShowcaseMediaAsset[];
  alt: string;
  fallbackTitle: string;
  fallbackDescription: string;
  previousLabel: string;
  nextLabel: string;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeAsset = assets[activeIndex] ?? assets[0];

  if (!activeAsset) return null;

  const selectPrevious = () => {
    setActiveIndex((index) => Math.max(0, index - 1));
  };
  const selectNext = () => {
    setActiveIndex((index) => Math.min(assets.length - 1, index + 1));
  };

  return (
    <div
      role="region"
      aria-roledescription="carousel"
      aria-label={alt}
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') selectPrevious();
        if (event.key === 'ArrowRight') selectNext();
      }}
    >
      <div className="mb-6 flex flex-wrap justify-center gap-2 md:mb-8 md:gap-3">
        {assets.map((asset, index) => (
          <button
            key={`${asset.url}-${index}`}
            type="button"
            onClick={() => setActiveIndex(index)}
            aria-current={index === activeIndex ? 'true' : undefined}
            className={cn(
              'rounded-full px-4 py-2 text-[clamp(0.75rem,0.9vw,0.8125rem)] leading-[1.45] transition-colors md:px-5',
              index === activeIndex
                ? 'bg-marketing-fg text-marketing-bg'
                : 'bg-marketing-accent-subtle text-marketing-accent-fg hover:bg-marketing-border'
            )}
          >
            {asset.label ?? String(index + 1).padStart(2, '0')}
          </button>
        ))}
      </div>

      <div
        aria-live="polite"
        aria-label={`${alt} ${activeIndex + 1}/${assets.length}`}
        className="bg-marketing-accent-subtle grid min-h-112 overflow-hidden rounded-2xl md:grid-cols-2 md:rounded-3xl"
      >
        <div className="flex min-w-0 flex-col justify-center px-7 py-10 text-left sm:px-10 md:px-12 lg:px-16">
          <p className="text-marketing-fg-subtle text-[clamp(0.6875rem,0.9vw,0.8125rem)] leading-[1.3] font-semibold [letter-spacing:-0.01em]">
            {activeAsset.label ?? String(activeIndex + 1).padStart(2, '0')}
          </p>
          <h3 className="text-marketing-fg mt-4 max-w-lg text-[clamp(1.5rem,3.4vw,2.75rem)] leading-[1.1] font-bold [letter-spacing:-0.03em] text-balance">
            {activeAsset.title?.trim() || fallbackTitle}
          </h3>
          <p className="text-marketing-fg-muted mt-4 max-w-md text-[clamp(0.9375rem,1.3vw,1.25rem)] leading-[1.35] font-semibold [letter-spacing:-0.015em] text-pretty">
            {activeAsset.description?.trim() || fallbackDescription}
          </p>
        </div>

        <div className="relative min-h-72 md:min-h-full">
          <Image
            src={activeAsset.url}
            alt={alt}
            fill
            unoptimized
            sizes="(min-width: 768px) 50vw, 100vw"
            className="object-cover"
          />
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between md:mt-6">
        <button
          type="button"
          onClick={selectPrevious}
          disabled={activeIndex === 0}
          className="text-marketing-fg flex items-center gap-2 text-[clamp(0.75rem,0.9vw,0.8125rem)] leading-[1.45] disabled:opacity-35"
        >
          <span className="bg-marketing-accent-subtle flex size-9 items-center justify-center rounded-full">
            <ArrowLeft className="size-4" aria-hidden="true" />
          </span>
          <span className="hidden sm:inline">{previousLabel}</span>
        </button>

        <button
          type="button"
          onClick={selectNext}
          disabled={activeIndex === assets.length - 1}
          className="text-marketing-fg flex items-center gap-2 text-[clamp(0.75rem,0.9vw,0.8125rem)] leading-[1.45] disabled:opacity-35"
        >
          <span className="hidden sm:inline">{nextLabel}</span>
          <span className="bg-marketing-accent-subtle flex size-9 items-center justify-center rounded-full">
            <ArrowRight className="size-4" aria-hidden="true" />
          </span>
        </button>
      </div>
    </div>
  );
}
